import { memo, useCallback, useMemo, useState } from "react";
import { Pressable } from "react-native";
import "@visioni/a3s-ui/theme";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Icon } from "@visioni/a3s-ui/components";
import { AccessCard } from "../AccessCard";
import { AccessListHeader } from "../AccessListHeader";

export type CameraAccessListCamera = {
  id: string;
  name: string;
  siteId: string;
  siteName: string;
  snapshotUrl?: string | null;
  /** Legacy (A3) cameras cannot be edited. */
  legacy?: boolean;
};

export type CameraPermission = {
  cameraId: string;
  role: "manager" | "viewer";
};

export type CameraAccessListProps = {
  cameras: CameraAccessListCamera[];
  selected: Map<string, "manager" | "viewer">;
  onToggle: (id: string) => void;
  onRoleChange: (id: string, role: "manager" | "viewer") => void;
};

type SiteGroup = {
  siteId: string;
  siteName: string;
  cameras: CameraAccessListCamera[];
};

function groupBySite(cameras: CameraAccessListCamera[]): SiteGroup[] {
  const groups = new Map<string, SiteGroup>();
  const ordered: SiteGroup[] = [];

  for (const camera of cameras) {
    let group = groups.get(camera.siteId);
    if (!group) {
      group = { siteId: camera.siteId, siteName: camera.siteName, cameras: [] };
      groups.set(camera.siteId, group);
      ordered.push(group);
    }
    group.cameras.push(camera);
  }

  return ordered;
}

export const CameraAccessList = memo(({ cameras, selected, onToggle, onRoleChange }: CameraAccessListProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedSites, setCollapsedSites] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupBySite(cameras), [cameras]);

  const handleToggleSite = useCallback((siteId: string) => {
    setCollapsedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }, []);

  const handleToggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  return (
    <View style={styles.container}>
      <AccessListHeader
        title="Cameras"
        selectedCount={selected.size}
        totalCount={cameras.length}
        collapsed={collapsed}
        onToggle={handleToggleCollapsed}
      />
      {!collapsed &&
        groups.map((group) => (
          <SiteSection
            key={group.siteId}
            group={group}
            collapsed={collapsedSites.has(group.siteId)}
            selected={selected}
            onToggleSite={handleToggleSite}
            onToggleCamera={onToggle}
            onRoleChange={onRoleChange}
          />
        ))}
    </View>
  );
});

const SiteSection = memo(
  ({
    group,
    collapsed,
    selected,
    onToggleSite,
    onToggleCamera,
    onRoleChange,
  }: {
    group: SiteGroup;
    collapsed: boolean;
    selected: Map<string, "manager" | "viewer">;
    onToggleSite: (siteId: string) => void;
    onToggleCamera: (cameraId: string) => void;
    onRoleChange: (cameraId: string, role: "manager" | "viewer") => void;
  }) => {
    const { theme } = useUnistyles();

    const handlePress = useCallback(() => {
      onToggleSite(group.siteId);
    }, [onToggleSite, group.siteId]);

    const selectedCount = group.cameras.filter((c) => selected.has(c.id)).length;

    return (
      <View style={styles.siteSection}>
        <Pressable onPress={handlePress} style={styles.siteHeader}>
          <View style={styles.siteHeaderContent}>
            <Text textStyle="sm/semibold">{group.siteName}</Text>
            <Text textStyle="xs/regular" color="textMuted">
              {selectedCount}/{group.cameras.length}
            </Text>
          </View>
          <Icon name={collapsed ? "chevronDown" : "chevronUp"} size={16} color={theme.colors.textMuted} />
        </Pressable>

        {!collapsed && (
          <View style={styles.cameraList}>
            {group.cameras.map((camera) => (
              <AccessCard
                key={camera.id}
                id={camera.id}
                name={camera.name}
                thumbnailUrl={camera.snapshotUrl}
                selected={selected.has(camera.id)}
                role={selected.get(camera.id) ?? "viewer"}
                disabled={camera.legacy === true}
                onToggle={onToggleCamera}
                onRoleChange={onRoleChange}
              />
            ))}
          </View>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    padding: theme.spacing.md,
  },
  siteSection: {
    gap: theme.spacing.sm,
    paddingLeft: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    padding: theme.spacing.md,
  },
  siteHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    userSelect: "none",
  },
  siteHeaderContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  cameraList: {
    gap: theme.spacing.sm,
  },
}));
